import { useEffect, useState } from "react";

import { useShowPriceInVat } from "hooks";
import { TYRE_REPLACEMENT_SOURCES } from "models";
import { useBrandOptions } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/hooks/useBrandOptions";
import { useReplacementTyreSearch, useTyreNet, useTyreNetStock, useTyreTeam, useTyreTeamStock } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { tyresToReplacementOffers } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { getDisplayedPrice, getPriceWithoutVat } from "utils/common";

import type { DealerLocation, SEASONS, Tyre, TyreDiscount, VehicleTyre, VehicleTyreReplacement } from "models";

type UseTyreReplacementOfferProps = {
  location: DealerLocation | undefined;
  existingReplacements: VehicleTyreReplacement[];
  vehicleTyre: VehicleTyre | null;
};

export interface ReplacementOffer extends Tyre {
  discount: number | null; // when done check if discount/net_margin_euro/percent are actually needed or not
  net_margin_euro: number | null;
  net_margin_percent: number | null;
  priceOnOtherPosition: number; // old name consistentPrice
  price: number;
  quantity: number;
  selected: boolean;
  source: TYRE_REPLACEMENT_SOURCES;
}

const toTyreDimension = (dimension: number | null) => (dimension !== null && dimension > 0 ? dimension : null);

export const useTyreReplacementOffer = ({ location, vehicleTyre, existingReplacements }: UseTyreReplacementOfferProps) => {
  const [selectedDiscount, setSelectedDiscount] = useState<TyreDiscount | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [size, setSize] = useState<number | null>(null);
  const [season, setSeason] = useState<SEASONS | null>(null);
  const [manufacturer, setManufacturer] = useState("");
  const [inStock, setInStock] = useState(true);
  const [searchResultOffers, setSearchResultOffers] = useState<ReplacementOffer[]>([]);
  const [existingTyreReplacementsWithSameWidthOffers, setExistingTyreReplacementsWithSameWidthOffers] = useState<ReplacementOffer[]>([]);
  const [triggerInitialSearch, setTriggerInitialSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchingTyres, setIsSearchingTyres] = useState(false);

  const tyreNet = useTyreNet();
  const tyreTeam = useTyreTeam(location);
  const showPriceInVat = useShowPriceInVat();
  const { tyreBrandOptions, isFetchingBrands } = useBrandOptions(selectedDiscount, location);

  const isTyreTeamEnabled = location?.is_tyre_team_enabled === true;
  const isTyreNetEnabled = location?.is_tyre_net_enabled === true;
  const isExternalCatalogEnabled = isTyreTeamEnabled || isTyreNetEnabled;
  const source = isTyreTeamEnabled ? TYRE_REPLACEMENT_SOURCES.TyreTeam : isTyreNetEnabled ? TYRE_REPLACEMENT_SOURCES.TyreNet : TYRE_REPLACEMENT_SOURCES.Claire;

  const { selectedDeliveryIds, setSelectedDeliveryIds, isLoadingTyreDeliveries, getDeliveriesOptions, getDeliveryChannelName, getDeliveryPopup } = isTyreTeamEnabled
    ? tyreTeam
    : tyreNet;

  const notInQuestionResultReplacements = (ean: string) => !vehicleTyre?.replacements?.some(tr => tr.tyre?.ean === ean);

  // this is simpler than trying to process them when the search is done and when stock for existing replacements is done, as it can race
  // must not change references, only filter+combine! see handleSelectionUpdate / handlePriceUpdate
  const combinedReplacementOffers = [
    ...searchResultOffers.filter(sr => sr.ean && notInQuestionResultReplacements(sr.ean) && !existingTyreReplacementsWithSameWidthOffers.some(sw => sw.ean === sr.ean)),
    ...existingTyreReplacementsWithSameWidthOffers.filter(o => o.ean && notInQuestionResultReplacements(o.ean))
  ];

  let existingTyreReplacementWithSameWidth: VehicleTyreReplacement[] = [];
  if (existingReplacements.length) {
    const uniqueExistingReplacement = new Map<string, VehicleTyreReplacement>();
    existingReplacements.forEach(er => {
      if (er.tyre?.ean && notInQuestionResultReplacements(er.tyre.ean) && er.tyre.width === vehicleTyre?.tyre?.width) uniqueExistingReplacement.set(er.tyre.ean, er);
    });

    existingTyreReplacementWithSameWidth = uniqueExistingReplacement.values().toArray();
  }

  const tyreTeamStock = useTyreTeamStock(isTyreTeamEnabled, existingTyreReplacementWithSameWidth);
  const tyreNetStock = useTyreNetStock(isTyreNetEnabled, existingTyreReplacementWithSameWidth);
  const { data: stock, isFetching: isFetchingStock } = isTyreTeamEnabled ? tyreTeamStock : tyreNetStock;

  useEffect(() => setExistingTyreReplacementsWithSameWidthOffers(stock?.length ? tyresToReplacementOffers(source, stock, selectedDiscount) : []), [stock]);

  const { searchReplacementTyres, isSearchReplacementTyresFetching } = useReplacementTyreSearch(
    {
      height,
      dealer_location_id: location?.id ?? 0,
      manufacturer: tyreBrandOptions.find(bo => bo.value === manufacturer)?.text || null,
      season,
      size,
      width,
      in_stock: isExternalCatalogEnabled ? inStock : undefined,
      tyre_team_delivery_ids: isTyreTeamEnabled ? selectedDeliveryIds : undefined
    },
    location
  );

  const handleSearchTyres = async () => {
    const { data: searchResults = [] } = await searchReplacementTyres();
    const searchResultOffers = tyresToReplacementOffers(source, searchResults, selectedDiscount);
    searchResultOffers.forEach(to => (to.priceOnOtherPosition = existingReplacements.find(tr => tr.tyre?.ean === to.ean)?.price ?? 0));
    setSearchResultOffers(searchResultOffers);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!isSearchingTyres) return;

    setIsSearchingTyres(false);
    if (!width) return;

    handleSearchTyres();
  }, [isSearchingTyres]);

  useEffect(() => {
    if (!vehicleTyre?.tyre) return;
    const { width, height, size, manufacturer, season } = vehicleTyre.tyre;

    setWidth(toTyreDimension(width));
    setHeight(toTyreDimension(height));
    setSize(toTyreDimension(size));
    setManufacturer(manufacturer.toLocaleLowerCase());
    setSeason(season);
    setInStock(true);
    setSearchResultOffers([]);
    setTriggerInitialSearch(true);
  }, [vehicleTyre]);

  useEffect(() => {
    if (
      !triggerInitialSearch ||
      !location ||
      (isTyreTeamEnabled && !selectedDeliveryIds.length) ||
      !vehicleTyre?.tyre ||
      width !== toTyreDimension(vehicleTyre.tyre.width) ||
      height !== toTyreDimension(vehicleTyre.tyre.height) ||
      size !== toTyreDimension(vehicleTyre.tyre.size) ||
      manufacturer !== vehicleTyre.tyre.manufacturer.toLocaleLowerCase() ||
      season !== vehicleTyre.tyre.season ||
      !inStock
    )
      return;

    setTriggerInitialSearch(false);
    handleSearchTyres();
  }, [triggerInitialSearch, location, isTyreNetEnabled, selectedDeliveryIds, vehicleTyre, width, height, size, manufacturer, season, inStock]);

  const handleSelectDiscount = async (discount: TyreDiscount | null) => {
    let newManufacturer = manufacturer;

    if (discount?.is_brand_discount_only && discount.tyre_brand_discounts?.length) {
      newManufacturer = discount.tyre_brand_discounts[0].brand.toLocaleLowerCase();

      if (manufacturer !== newManufacturer) setManufacturer(newManufacturer);
    } else if (selectedDiscount?.is_brand_discount_only && selectedDiscount.tyre_brand_discounts?.length) {
      newManufacturer = (vehicleTyre?.tyre?.manufacturer || "").toLocaleLowerCase();
      setManufacturer(newManufacturer);
    }

    setSelectedDiscount(discount);
    setExistingTyreReplacementsWithSameWidthOffers(offers => tyresToReplacementOffers(source, offers, discount));
    setSearchResultOffers(searchResults => tyresToReplacementOffers(source, searchResults, discount));

    if (width && newManufacturer && newManufacturer !== manufacturer) handleSearchTyres();
  };

  const handleSelectionUpdate = (offer: ReplacementOffer, selected: boolean) => {
    setExistingTyreReplacementsWithSameWidthOffers(offers => offers.map(o => (o === offer ? { ...o, selected } : o)));
    setSearchResultOffers(searchResults => searchResults.map(sr => (sr === offer ? { ...sr, selected } : sr)));
  };

  const getDisplayedOfferPrice = (price: number) => getDisplayedPrice(price, location, showPriceInVat);

  const handlePriceUpdate = (offer: ReplacementOffer, displayedPrice: number) => {
    const price = showPriceInVat ? getPriceWithoutVat(displayedPrice, location) : displayedPrice;

    setExistingTyreReplacementsWithSameWidthOffers(offers => offers.map(o => (o === offer ? { ...o, price } : o)));
    setSearchResultOffers(searchResults => searchResults.map(sr => (sr === offer ? { ...sr, price } : sr)));
  };

  return {
    combinedReplacementOffers,
    prefilledDimensions: {
      width: toTyreDimension(vehicleTyre?.tyre?.width ?? null),
      height: toTyreDimension(vehicleTyre?.tyre?.height ?? null),
      size: toTyreDimension(vehicleTyre?.tyre?.size ?? null)
    },
    currentPage,
    setCurrentPage,
    selectedDiscount,
    handleSelectDiscount,
    width,
    setWidth: (dimension: number | null) => setWidth(toTyreDimension(dimension)),
    height,
    setHeight: (dimension: number | null) => setHeight(toTyreDimension(dimension)),
    size,
    setSize: (dimension: number | null) => setSize(toTyreDimension(dimension)),
    manufacturer,
    setManufacturer,
    tyreBrandOptions,
    isFetchingBrands,
    season,
    setSeason,
    isLoadingTyreDeliveries,
    selectedDeliveryIds,
    setSelectedDeliveryIds,
    getDeliveriesOptions,
    getDeliveryChannelName,
    getDeliveryPopup,
    inStock,
    setInStock,
    isSearching: isSearchReplacementTyresFetching || isFetchingStock,
    handleSearch: () => setIsSearchingTyres(true),
    handlePriceUpdate,
    handleSelectionUpdate,
    isExternalCatalogEnabled,
    showPriceInVat,
    getDisplayedOfferPrice
  };
};
